import { lazy, Suspense, useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import Lenis from "lenis";
import { Toaster } from "@/components/ui/sonner";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { CookieBanner } from "@/components/CookieBanner";
import { StickyCTA } from "@/components/StickyCTA";
import Home from "@/pages/Home";

const Services = lazy(() => import("@/pages/Services"));
const Pricing = lazy(() => import("@/pages/Pricing"));
const HowItWorks = lazy(() => import("@/pages/HowItWorks"));
const About = lazy(() => import("@/pages/About"));
const BookDemo = lazy(() => import("@/pages/BookDemo"));
const Contact = lazy(() => import("@/pages/Contact"));
const Privacy = lazy(() => import("@/pages/Privacy"));
const Terms = lazy(() => import("@/pages/Terms"));
const NotFound = lazy(() => import("@/pages/NotFound"));

const PAGE_META = {
  "/": {
    title: "LeadCerebro — Affordable Lead-Generating Websites for Small Business, Live in 72 Hours",
    description:
      "A website that does more than look good — it gets you leads. Custom mobile-first small business website with hosting included for a flat $800. First draft in 48 hours, live in 72.",
  },
  "/services": {
    title: "What's Included — Services | LeadCerebro",
    description:
      "Everything in the $800 LeadCerebro package: up to 5 custom pages, lead capture form, Calendly embed, on-page SEO, SSL and 1 year of hosting — plus exactly what's not included.",
  },
  "/pricing": {
    title: "Pricing — $800 Flat, Hosting Included | LeadCerebro",
    description:
      "One flat $800 price for a complete lead-generating website, hosting included for a year. See the honest comparison vs agencies, DIY builders and freelancers.",
  },
  "/how-it-works": {
    title: "How It Works — First Draft in 48h, Live in 72h | LeadCerebro",
    description:
      "From free demo call to a live, lead-generating website in 72 hours. See the full hour-by-hour LeadCerebro process and the short list of what we need from you.",
  },
  "/about": {
    title: "About — Websites That Earn Their Keep | LeadCerebro",
    description:
      "Why LeadCerebro exists: a third option between $10k agencies and lost DIY weekends. Our mission, our lead-first philosophy, and how we work.",
  },
  "/book-demo": {
    title: "Book a Free Demo — 20 Minutes, No Obligation | LeadCerebro",
    description:
      "Book your free 20-minute LeadCerebro demo. See what your lead-generating website would look like before you spend anything. No credit card required.",
  },
  "/contact": {
    title: "Contact Us — Ask Before You Spend a Cent | LeadCerebro",
    description:
      "Questions about pricing, scope, timelines or the refund policy? Contact LeadCerebro — we usually reply within one business day.",
  },
  "/privacy": {
    title: "Privacy Policy | LeadCerebro",
    description: "How LeadCerebro collects, uses and protects your data — written in plain language.",
  },
  "/terms": {
    title: "Terms of Service | LeadCerebro",
    description:
      "LeadCerebro terms in plain language: the $800 package, 48h/72h timeline, payment terms, hosting renewal and the no-refund policy.",
  },
};

const RouteMeta = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    const meta = PAGE_META[pathname] || {
      title: "Page Not Found | LeadCerebro",
      description: "The page you're looking for doesn't exist.",
    };
    document.title = meta.title;
    const tag = document.querySelector('meta[name="description"]');
    if (tag) tag.setAttribute("content", meta.description);
  }, [pathname]);
  return null;
};

const ScrollManager = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    if (window.__lenis) {
      window.__lenis.scrollTo(0, { immediate: true });
    } else {
      window.scrollTo(0, 0);
    }
  }, [pathname]);
  return null;
};

function App() {
  useEffect(() => {
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return undefined;
    const lenis = new Lenis({ duration: 1.1, smoothWheel: true });
    window.__lenis = lenis;
    let raf;
    const loop = (time) => {
      lenis.raf(time);
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => {
      cancelAnimationFrame(raf);
      lenis.destroy();
      window.__lenis = null;
    };
  }, []);

  return (
    <BrowserRouter>
      <ScrollManager />
      <RouteMeta />
      <div className="min-h-screen bg-ink text-slate-100">
        <Header />
        <main>
          <Suspense fallback={<div className="min-h-[70vh]" />}>
            <Routes>
              <Route path="/" element={<Home />} />
              <Route path="/services" element={<Services />} />
              <Route path="/pricing" element={<Pricing />} />
              <Route path="/how-it-works" element={<HowItWorks />} />
              <Route path="/about" element={<About />} />
              <Route path="/book-demo" element={<BookDemo />} />
              <Route path="/contact" element={<Contact />} />
              <Route path="/privacy" element={<Privacy />} />
              <Route path="/terms" element={<Terms />} />
              <Route path="*" element={<NotFound />} />
            </Routes>
          </Suspense>
        </main>
        <Footer />
        <StickyCTA />
        <CookieBanner />
        <Toaster position="bottom-right" theme="dark" />
      </div>
    </BrowserRouter>
  );
}

export default App;
